import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tile,
  Button,
  Toggle,
  Tag,
  Modal,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  Slider,
  Grid,
  Column,
  InlineNotification,
  Loading,
  Dropdown,
  NumberInput,
  Search
} from '@carbon/react';
import { 
  Add, 
  Edit, 
  TrashCan, 
  Checkmark, 
  Warning, 
  Information, 
  Temperature, 
  Wifi, 
  Notification,
  ArrowRight,
  Grid as GridIcon,
  List,
  Humidity,
  BatteryLow
} from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useSubscription } from '../hooks/useSubscription';
import { useAlertRules } from '../hooks/useAlertRules';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { useZones } from '../hooks/useZones';
import { supabase } from '../services/supabaseClient';
import SubscriptionGate from '../components/SubscriptionGate';

const RulesPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { canAddRules } = useSubscription();
  const { rules: supabaseRules, isLoading, error, createRule, updateRule, deleteRule, toggleActive } = useAlertRules();
  const { sensors } = useSensorsOptimized();
  const isDark = theme === 'dark';
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  
  // Pagination state (only used for table view)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  // Format creation date
  const formatCreatedDate = (createdAt) => {
    if (!createdAt) return 'Unknown';
    
    const createdDate = new Date(createdAt);
    const now = new Date();
    const diffMs = now - createdDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffDays < 1) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours < 1) {
        const diffMins = Math.floor(diffMs / (1000 * 60));
        return diffMins < 1 ? 'Just now' : `${diffMins} min ago`;
      }
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffMonths < 1) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffMonths < 12) {
      return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    } else {
      return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
    }
  };

  // Map Supabase rules to UI format
  const rules = useMemo(() => {
    return supabaseRules.map(rule => {
      // Get sensor names from sensor IDs
      const deviceNames = rule.sensorIds
        .map(sensorId => {
          const sensor = sensors.find(s => s.id === sensorId);
          return sensor ? sensor.name : null;
        })
        .filter(name => name !== null);

      // Format alert type for UI
      let alertType = 'PUSH';
      if (rule.alertType === 'sms') {
        alertType = 'SMS';
      } else if (rule.alertType === 'both') {
        alertType = 'PUSH + SMS';
      }

      // Build thresholds object
      const thresholds = {};
      if (rule.tempMinEnabled && rule.tempMinValue !== null && rule.tempMinValue !== undefined) {
        thresholds.tempMin = rule.tempMinValue;
      }
      if (rule.tempMaxEnabled && rule.tempMaxValue !== null && rule.tempMaxValue !== undefined) {
        thresholds.tempMax = rule.tempMaxValue;
      }
      if (rule.humidityMinEnabled && rule.humidityMinValue !== null && rule.humidityMinValue !== undefined) {
        thresholds.humidityMin = rule.humidityMinValue;
      }
      if (rule.humidityMaxEnabled && rule.humidityMaxValue !== null && rule.humidityMaxValue !== undefined) {
        thresholds.humidityMax = rule.humidityMaxValue;
      }
      if (rule.batteryLowEnabled) {
        thresholds.lowBattery = true;
        if (rule.batteryLowValue !== null && rule.batteryLowValue !== undefined) {
          thresholds.batteryLowValue = rule.batteryLowValue;
        } else {
          thresholds.batteryLowValue = 20; // Default value
        }
      }

      return {
        id: rule.id,
        name: rule.ruleName,
        description: rule.description || '',
        alertType: alertType,
        isActive: rule.isActive,
        devices: deviceNames,
        thresholds: thresholds,
        createdAt: formatCreatedDate(rule.createdAt),
        // Keep original rule for editing
        originalRule: rule,
      };
    });
  }, [supabaseRules, sensors]);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    alertType: 'PUSH',
    phoneNumber: '+1234567890',
    minTempEnabled: false,
    maxTempEnabled: false,
    minTempValue: 0,
    maxTempValue: 30,
    minHumidityEnabled: false,
    maxHumidityEnabled: false,
    minHumidityValue: 20,
    maxHumidityValue: 80,
    lowBatteryEnabled: false,
    batteryLowValue: 20,
    cooldownPeriod: 30,
    timeWindowEnabled: false,
    timeWindowStart: '09:00',
    timeWindowEnd: '17:00',
    timeWindowDays: [1, 2, 3, 4, 5],
    selectedSensors: [],
  });

  const filteredRules = rules.filter(rule => {
    const matchesSearch = rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rule.alertType.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && rule.isActive) ||
      (statusFilter === 'inactive' && !rule.isActive);
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations (only for table view)
  const totalItems = filteredRules.length;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedRules = viewMode === 'table' 
    ? filteredRules.slice(startIndex, endIndex)
    : filteredRules;

  // Reset to page 1 when search term, view mode, or status filter changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, viewMode, statusFilter]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      alertType: 'PUSH',
      phoneNumber: '+1234567890',
      minTempEnabled: false,
      maxTempEnabled: false,
      minTempValue: 0,
      maxTempValue: 30,
      minHumidityEnabled: false,
      maxHumidityEnabled: false,
      minHumidityValue: 20,
      maxHumidityValue: 80,
      lowBatteryEnabled: false,
      batteryLowValue: 20,
      cooldownPeriod: 30,
      timeWindowEnabled: false,
      timeWindowStart: '09:00',
      timeWindowEnd: '17:00',
      timeWindowDays: [1, 2, 3, 4, 5],
      selectedSensors: [],
    });
  };

  const handleCreateRule = () => {
    resetForm();
    setEditingRule(null);
    setShowCreateModal(true);
  };

  const handleEditRule = (rule) => {
    const originalRule = rule.originalRule;
    setFormData({
      name: rule.name,
      description: rule.description,
      alertType: rule.alertType,
      phoneNumber: originalRule?.smsPhoneNumber || '',
      minTempEnabled: originalRule?.tempMinEnabled || false,
      maxTempEnabled: originalRule?.tempMaxEnabled || false,
      minTempValue: originalRule?.tempMinValue || 0,
      maxTempValue: originalRule?.tempMaxValue || 30,
      minHumidityEnabled: originalRule?.humidityMinEnabled || false,
      maxHumidityEnabled: originalRule?.humidityMaxEnabled || false,
      minHumidityValue: originalRule?.humidityMinValue || 20,
      maxHumidityValue: originalRule?.humidityMaxValue || 80,
      lowBatteryEnabled: originalRule?.batteryLowEnabled || false,
      batteryLowValue: originalRule?.batteryLowValue || 20,
      cooldownPeriod: originalRule?.cooldownMinutes || 30,
      timeWindowEnabled: originalRule?.timeWindowEnabled || false,
      timeWindowStart: originalRule?.timeWindowStart || '09:00',
      timeWindowEnd: originalRule?.timeWindowEnd || '17:00',
      timeWindowDays: originalRule?.timeWindowDays || [1, 2, 3, 4, 5],
      selectedSensors: originalRule?.sensorIds || [],
    });
    setEditingRule(rule);
    setShowCreateModal(true);
  };

  const handleSaveRule = async () => {
    if (!formData.name.trim()) {
      alert('Rule name is required');
      return;
    }

    // Get selected sensor IDs from form
    const selectedSensorIds = formData.selectedSensors && formData.selectedSensors.length > 0 
      ? formData.selectedSensors 
      : (editingRule?.originalRule?.sensorIds || []);

    if (selectedSensorIds.length === 0) {
      alert('Please select at least one device');
      return;
    }

    // Map alert type
    let alertType = 'push';
    if (formData.alertType === 'SMS') {
      alertType = 'sms';
    } else if (formData.alertType === 'PUSH + SMS') {
      alertType = 'both';
    }

    if ((alertType === 'sms' || alertType === 'both') && !formData.phoneNumber?.trim()) {
      alert('SMS phone number is required for SMS alerts');
      return;
    }

    const ruleData = {
      ruleName: formData.name.trim(),
      description: formData.description.trim() || undefined,
      alertType: alertType,
      sensorIds: selectedSensorIds,
      tempMinEnabled: formData.minTempEnabled,
      tempMinValue: formData.minTempEnabled ? formData.minTempValue : undefined,
      tempMaxEnabled: formData.maxTempEnabled,
      tempMaxValue: formData.maxTempEnabled ? formData.maxTempValue : undefined,
      humidityMinEnabled: formData.minHumidityEnabled,
      humidityMinValue: formData.minHumidityEnabled ? formData.minHumidityValue : undefined,
      humidityMaxEnabled: formData.maxHumidityEnabled,
      humidityMaxValue: formData.maxHumidityEnabled ? formData.maxHumidityValue : undefined,
      batteryLowEnabled: formData.lowBatteryEnabled,
      batteryLowValue: formData.lowBatteryEnabled ? (formData.batteryLowValue || 20) : undefined,
      smsPhoneNumber: (alertType === 'sms' || alertType === 'both') ? formData.phoneNumber.trim() : undefined,
      cooldownMinutes: formData.cooldownPeriod || 30,
      timeWindowEnabled: formData.timeWindowEnabled || false,
      timeWindowStart: formData.timeWindowEnabled ? formData.timeWindowStart : undefined,
      timeWindowEnd: formData.timeWindowEnabled ? formData.timeWindowEnd : undefined,
      timeWindowDays: formData.timeWindowEnabled ? formData.timeWindowDays : undefined,
    };

    try {
      if (editingRule) {
        await updateRule(editingRule.id, ruleData);
      } else {
        await createRule(ruleData);
      }
      setShowCreateModal(false);
      resetForm();
    } catch (err) {
      alert(err.message || 'Failed to save rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await deleteRule(ruleId);
      setDeleteConfirm(null);
    } catch (err) {
      alert(err.message || 'Failed to delete rule');
    }
  };

  const toggleRuleActive = async (ruleId) => {
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;
    
    try {
      await toggleActive(ruleId, !rule.isActive);
    } catch (err) {
      alert(err.message || 'Failed to update rule status');
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? '#24a148' : '#da1e28';
  };

  const getStatusIcon = (isActive) => {
    return isActive
      ? <CheckmarkFilled size={16} style={{ color: '#24a148' }} />
      : <CloseOutline size={16} style={{ color: '#da1e28' }} />;
  };

  const getThresholdSummary = (thresholds) => {
    const parts = [];
    if (thresholds.tempMin !== undefined && thresholds.tempMin !== null) parts.push(`Min: ${thresholds.tempMin}°C`);
    if (thresholds.tempMax !== undefined && thresholds.tempMax !== null) parts.push(`Max: ${thresholds.tempMax}°C`);
    if (thresholds.humidityMin !== undefined && thresholds.humidityMin !== null) parts.push(`Humidity Min: ${thresholds.humidityMin}%`);
    if (thresholds.humidityMax !== undefined && thresholds.humidityMax !== null) parts.push(`Humidity Max: ${thresholds.humidityMax}%`);
    if (thresholds.lowBattery) {
      if (thresholds.batteryLowValue !== undefined && thresholds.batteryLowValue !== null) {
        parts.push(`Low Battery: ${thresholds.batteryLowValue}%`);
      } else {
        parts.push('Low Battery');
      }
    }
    return parts.length > 0 ? parts.join(', ') : 'No thresholds';
  };

  const getAlertTypeTag = (alertType) => {
    switch (alertType) {
      case 'SMS':
        return <Tag type="purple" size="sm">SMS</Tag>;
      case 'PUSH':
        return <Tag type="blue" size="sm">PUSH</Tag>;
      case 'PUSH + SMS':
        return <Tag type="teal" size="sm">PUSH + SMS</Tag>;
      default:
        return <Tag type="gray" size="sm">{alertType}</Tag>;
    }
  };

  if (isLoading) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }} data-testid="rules-page">
        <Loading description="Loading alert rules..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '24px' }} data-testid="rules-page">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <InlineNotification
              kind="error"
              title="Error loading alert rules"
              subtitle={error}
            />
          </Column>
        </Grid>
      </div>
    );
  }

  return (
    <SubscriptionGate 
      feature="creating and managing alert rules" 
      disabled={!canAddRules}
      customMessage="You need an active subscription to create and manage alert rules for your cold chain monitoring system."
    >
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="rules-page">
      {/* Header */}
      <div style={{ backgroundColor: bgSecondary, borderBottom: `1px solid ${borderColor}`, padding: '24px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <h1 style={{ color: textPrimary, fontSize: '1.75rem', fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Notification size={28} /> Alert Rules
                  </h1>
                  <p style={{ color: textSecondary, fontSize: '0.875rem' }}>{filteredRules.length} of {rules.length} rules</p>
                </div>
                <Button kind="primary" renderIcon={Add} onClick={handleCreateRule} data-testid="add-rule">
                  Add Rule
                </Button>
              </div>

              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'nowrap' }}>
                  <div style={{ flex: '1 1 300px', maxWidth: '400px' }}>
                    <Search
                      labelText="Search"
                      placeholder="Search rules..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size="lg"
                      data-testid="search-rules"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                    <Button
                      kind={viewMode === 'cards' ? 'secondary' : 'ghost'}
                      size="sm"
                      hasIconOnly
                      renderIcon={GridIcon}
                      iconDescription="Card view"
                      onClick={() => setViewMode('cards')}
                    />
                    <Button
                      kind={viewMode === 'table' ? 'secondary' : 'ghost'}
                      size="sm"
                      hasIconOnly
                      renderIcon={List}
                      iconDescription="Table view"
                      onClick={() => setViewMode('table')}
                    />
                  </div>
                </div>
                <div style={{ minWidth: '200px', flexShrink: 0, marginLeft: 'auto' }}>
                  <Dropdown
                    id="status-filter"
                    titleText="Filter by Status"
                    label="Select status"
                    items={['All Rules', 'Active', 'Inactive']}
                    selectedItem={statusFilter === 'all' ? 'All Rules' : 
                      statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                    onChange={({ selectedItem }) => {
                      if (selectedItem) {
                        const statusMap = {
                          'All Rules': 'all',
                          'Active': 'active',
                          'Inactive': 'inactive',
                        };
                        setStatusFilter(statusMap[selectedItem] || 'all');
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </Column>
        </Grid>
      </div>

      {/* Content */}
      <div style={{ padding: '24px 0' }}>
        <Grid>
          {viewMode === 'cards' ? (
            // Card View
            filteredRules.length === 0 ? (
              <Column lg={16} md={8} sm={4}>
                <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '48px', textAlign: 'center' }}>
                  <Notification size={48} style={{ color: textSecondary, marginBottom: '16px' }} />
                  <h3 style={{ color: textPrimary, marginBottom: '8px' }}>No Alert Rules</h3>
                  <p style={{ color: textSecondary, marginBottom: '24px' }}>Create your first alert rule to get notified about sensor events</p>
                  <Button kind="primary" renderIcon={Add} onClick={handleCreateRule}>
                    Create Rule
                  </Button>
                </Tile>
              </Column>
            ) : (
              filteredRules.map((rule) => (
                <Column lg={8} md={4} sm={4} key={rule.id}>
                  <Tile
                    style={{
                      backgroundColor: bgSecondary,
                      border: `1px solid ${borderColor}`,
                      borderLeft: `4px solid ${getStatusColor(rule.isActive)}`,
                      padding: '0',
                      marginBottom: '16px',
                      overflow: 'hidden',
                    }}
                    data-testid={`rule-card-${rule.id}`}
                  >
                    {/* Card Header */}
                    <div style={{ padding: '16px', borderBottom: `1px solid ${borderColor}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ backgroundColor: '#0f62fe', borderRadius: '8px', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Notification size={20} style={{ color: '#ffffff' }} />
                          </div>
                          <div>
                            <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, margin: 0, marginBottom: '4px' }}>
                              {rule.name}
                            </h3>
                            {getAlertTypeTag(rule.alertType)}
                          </div>
                        </div>
                        <Toggle
                          id={`toggle-${rule.id}`}
                          toggled={rule.isActive}
                          onToggle={() => toggleRuleActive(rule.id)}
                          hideLabel
                          size="sm"
                        />
                      </div>
                    </div>

                    {/* Card Body - Stats */}
                    <div style={{ padding: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                        <div>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>Status</p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {getStatusIcon(rule.isActive)}
                            <span style={{ color: getStatusColor(rule.isActive), fontWeight: 500 }}>
                              {rule.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                        <div>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>Devices</p>
                          <p style={{ color: '#0f62fe', fontWeight: 600, fontSize: '1.25rem', margin: 0 }}>{rule.devices.length}</p>
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>Thresholds</p>
                          <p style={{ color: textPrimary, margin: 0, fontSize: '0.875rem' }}>{getThresholdSummary(rule.thresholds)}</p>
                        </div>
                      </div>
                      <p style={{ color: textSecondary, fontSize: '0.875rem', marginTop: '12px', marginBottom: 0 }}>
                        {rule.description}
                      </p>
                      <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '4px', color: textSecondary, fontSize: '0.875rem' }}>
                        <Time size={14} />
                        <span>Created: {rule.createdAt}</span>
                      </div>
                    </div>

                    {/* Card Footer - Actions */}
                    <div style={{ padding: '12px 16px', borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                      <Button kind="ghost" size="sm" renderIcon={View} data-testid={`view-${rule.id}`}>View</Button>
                      <Button kind="ghost" size="sm" renderIcon={Edit} onClick={() => handleEditRule(rule)} data-testid={`edit-${rule.id}`}>Edit</Button>
                      <Button kind="danger--ghost" size="sm" renderIcon={TrashCan} onClick={() => setDeleteConfirm(rule.id)} data-testid={`delete-${rule.id}`}>Delete</Button>
                    </div>
                  </Tile>
                </Column>
              ))
            )
          ) : (
            // Table View
            <Column lg={16} md={8} sm={4}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: bgSecondary, border: `1px solid ${borderColor}` }}>
                  <thead>
                    <tr style={{ backgroundColor: isDark ? '#353535' : '#e0e0e0' }}>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Status</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Rule Name</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Alert Type</th>
                      <th style={{ padding: '16px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Devices</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Thresholds</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Created</th>
                      <th style={{ padding: '16px', textAlign: 'right', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRules.map((rule, idx) => (
                      <tr
                        key={rule.id}
                        style={{
                          borderBottom: `1px solid ${borderColor}`,
                          backgroundColor: idx % 2 === 0 ? bgSecondary : bgPrimary,
                        }}
                        data-testid={`rule-row-${rule.id}`}
                      >
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Toggle
                              id={`table-toggle-${rule.id}`}
                              toggled={rule.isActive}
                              onToggle={() => toggleRuleActive(rule.id)}
                              hideLabel
                              size="sm"
                            />
                            <span style={{ color: getStatusColor(rule.isActive), fontSize: '0.875rem' }}>
                              {rule.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div>
                            <p style={{ color: textPrimary, fontWeight: 500, marginBottom: '2px' }}>{rule.name}</p>
                            <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>{rule.description}</p>
                          </div>
                        </td>
                        <td style={{ padding: '16px' }}>
                          {getAlertTypeTag(rule.alertType)}
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          <span style={{ color: '#0f62fe', fontWeight: 600, fontSize: '1.125rem' }}>{rule.devices.length}</span>
                        </td>
                        <td style={{ padding: '16px', color: textSecondary, fontSize: '0.875rem', maxWidth: '200px' }}>
                          {getThresholdSummary(rule.thresholds)}
                        </td>
                        <td style={{ padding: '16px', color: textSecondary, fontSize: '0.875rem' }}>{rule.createdAt}</td>
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            <Button kind="ghost" size="sm" hasIconOnly renderIcon={View} iconDescription="View" />
                            <Button kind="ghost" size="sm" hasIconOnly renderIcon={Edit} iconDescription="Edit" onClick={() => handleEditRule(rule)} />
                            <Button kind="danger--ghost" size="sm" hasIconOnly renderIcon={TrashCan} iconDescription="Delete" onClick={() => setDeleteConfirm(rule.id)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination - only show for table view */}
              {totalItems > 0 && (
                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    backwardText="Previous"
                    forwardText="Next"
                    itemsPerPageText="Items per page:"
                    page={page}
                    pageNumberText="Page Number"
                    pageSize={pageSize}
                    pageSizes={[10, 20, 30, 40, 50]}
                    size="md"
                    totalItems={totalItems}
                    onChange={({ page, pageSize }) => {
                      setPage(page);
                      setPageSize(pageSize);
                    }}
                  />
                </div>
              )}
            </Column>
          )}
        </Grid>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        open={showCreateModal}
        onRequestClose={() => setShowCreateModal(false)}
        modalHeading={editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
        primaryButtonText={editingRule ? 'Save Changes' : 'Create Rule'}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSaveRule}
        primaryButtonDisabled={!formData.name}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <TextInput
            id="ruleName"
            labelText="Rule Name"
            placeholder="e.g., Freezer Temperature Alert"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <TextArea
            id="ruleDescription"
            labelText="Description"
            placeholder="Optional description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={2}
          />

          <div>
            <label style={{ color: textPrimary, fontSize: '0.75rem', fontWeight: 600, marginBottom: '8px', display: 'block' }}>Alert Type</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {['PUSH', 'SMS', 'PUSH + SMS'].map((type) => (
                <Button
                  key={type}
                  kind={formData.alertType === type ? 'primary' : 'tertiary'}
                  size="sm"
                  onClick={() => setFormData({ ...formData, alertType: type })}
                >
                  {type}
                </Button>
              ))}
            </div>
          </div>

          {(formData.alertType === 'SMS' || formData.alertType === 'PUSH + SMS') && (
            <TextInput
              id="phoneNumber"
              labelText="SMS Phone Number"
              placeholder="+1234567890"
              value={formData.phoneNumber}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            />
          )}

          {/* Device Selection */}
          <div>
            <label style={{ color: textPrimary, fontSize: '0.75rem', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
              Select Devices ({formData.selectedSensors?.length || 0} selected)
            </label>
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: `1px solid ${borderColor}`, borderRadius: '4px' }}>
              {sensors.map(sensor => (
                <div
                  key={sensor.id}
                  style={{
                    padding: '12px',
                    borderBottom: `1px solid ${borderColor}`,
                    backgroundColor: formData.selectedSensors?.includes(sensor.id) ? bgSecondary : bgPrimary,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                  onClick={() => {
                    const currentSelected = formData.selectedSensors || [];
                    const newSelected = currentSelected.includes(sensor.id)
                      ? currentSelected.filter(id => id !== sensor.id)
                      : [...currentSelected, sensor.id];
                    setFormData({ ...formData, selectedSensors: newSelected });
                  }}
                >
                  <div style={{ 
                    width: '20px', 
                    height: '20px', 
                    border: `2px solid ${formData.selectedSensors?.includes(sensor.id) ? '#0f62fe' : borderColor}`,
                    borderRadius: '4px',
                    backgroundColor: formData.selectedSensors?.includes(sensor.id) ? '#0f62fe' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {formData.selectedSensors?.includes(sensor.id) && (
                      <CheckmarkFilled size={14} style={{ color: '#ffffff' }} />
                    )}
                  </div>
                  <span style={{ 
                    color: textPrimary, 
                    fontWeight: formData.selectedSensors?.includes(sensor.id) ? 500 : 400 
                  }}>
                    {sensor.name}
                  </span>
                </div>
              ))}
              {sensors.length === 0 && (
                <div style={{ padding: '12px', color: textSecondary, textAlign: 'center' }}>
                  No sensors available
                </div>
              )}
            </div>
          </div>

          <div>
            <label style={{ color: textPrimary, fontSize: '0.75rem', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Temperature size={18} /> Temperature Thresholds
            </label>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ color: textSecondary, fontSize: '0.875rem' }}>Minimum Temperature Alert</span>
              <Toggle
                id="minTempToggle"
                toggled={formData.minTempEnabled}
                onToggle={() => setFormData({ ...formData, minTempEnabled: !formData.minTempEnabled })}
                hideLabel
                size="sm"
              />
            </div>
            {formData.minTempEnabled && (
              <Slider
                labelText="Min Temperature (°C)"
                value={formData.minTempValue}
                min={-50}
                max={50}
                step={1}
                onChange={({ value }) => setFormData({ ...formData, minTempValue: value })}
              />
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', marginTop: '16px' }}>
              <span style={{ color: textSecondary, fontSize: '0.875rem' }}>Maximum Temperature Alert</span>
              <Toggle
                id="maxTempToggle"
                toggled={formData.maxTempEnabled}
                onToggle={() => setFormData({ ...formData, maxTempEnabled: !formData.maxTempEnabled })}
                hideLabel
                size="sm"
              />
            </div>
            {formData.maxTempEnabled && (
              <Slider
                labelText="Max Temperature (°C)"
                value={formData.maxTempValue}
                min={-50}
                max={100}
                step={1}
                onChange={({ value }) => setFormData({ ...formData, maxTempValue: value })}
              />
            )}
          </div>

          <div>
            <label style={{ color: textPrimary, fontSize: '0.75rem', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Humidity size={18} /> Humidity Thresholds
            </label>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ color: textSecondary, fontSize: '0.875rem' }}>Minimum Humidity Alert</span>
              <Toggle
                id="minHumidityToggle"
                toggled={formData.minHumidityEnabled}
                onToggle={() => setFormData({ ...formData, minHumidityEnabled: !formData.minHumidityEnabled })}
                hideLabel
                size="sm"
              />
            </div>
            {formData.minHumidityEnabled && (
              <Slider
                labelText="Min Humidity (%)"
                value={formData.minHumidityValue}
                min={0}
                max={100}
                step={1}
                onChange={({ value }) => setFormData({ ...formData, minHumidityValue: value })}
              />
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', marginTop: '16px' }}>
              <span style={{ color: textSecondary, fontSize: '0.875rem' }}>Maximum Humidity Alert</span>
              <Toggle
                id="maxHumidityToggle"
                toggled={formData.maxHumidityEnabled}
                onToggle={() => setFormData({ ...formData, maxHumidityEnabled: !formData.maxHumidityEnabled })}
                hideLabel
                size="sm"
              />
            </div>
            {formData.maxHumidityEnabled && (
              <Slider
                labelText="Max Humidity (%)"
                value={formData.maxHumidityValue}
                min={0}
                max={100}
                step={1}
                onChange={({ value }) => setFormData({ ...formData, maxHumidityValue: value })}
              />
            )}
          </div>

          <div>
            <label style={{ color: textPrimary, fontSize: '0.75rem', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BatteryLow size={18} /> Battery Threshold
            </label>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ color: textSecondary, fontSize: '0.875rem' }}>Low Battery Alert</span>
              <Toggle
                id="lowBatteryToggle"
                toggled={formData.lowBatteryEnabled}
                onToggle={() => setFormData({ ...formData, lowBatteryEnabled: !formData.lowBatteryEnabled })}
                hideLabel
                size="sm"
              />
            </div>
            {formData.lowBatteryEnabled && (
              <Slider
                labelText="Battery Low Threshold (%)"
                value={formData.batteryLowValue || 20}
                min={0}
                max={50}
                step={5}
                onChange={({ value }) => setFormData({ ...formData, batteryLowValue: value })}
              />
            )}
          </div>

          {/* Cooldown Period */}
          <div>
            <label style={{ color: textPrimary, fontSize: '0.75rem', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
              Cooldown Period
            </label>
            <Slider
              labelText="Cooldown (minutes)"
              value={formData.cooldownPeriod || 30}
              min={5}
              max={120}
              step={5}
              onChange={({ value }) => setFormData({ ...formData, cooldownPeriod: value })}
            />
            <p style={{ color: textSecondary, fontSize: '0.75rem', marginTop: '4px' }}>
              Prevents alert spam by waiting this long between alerts for the same device
            </p>
          </div>

          {/* Time Window */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ color: textPrimary, fontSize: '0.75rem', fontWeight: 600, display: 'block' }}>
                Time Window Restriction
              </label>
              <Toggle
                id="timeWindowToggle"
                toggled={formData.timeWindowEnabled}
                onToggle={() => setFormData({ ...formData, timeWindowEnabled: !formData.timeWindowEnabled })}
                hideLabel
                size="sm"
              />
            </div>
            {formData.timeWindowEnabled && (
              <>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '8px', display: 'block' }}>Start Time</label>
                    <TextInput
                      id="timeWindowStart"
                      placeholder="09:00"
                      value={formData.timeWindowStart || '09:00'}
                      onChange={(e) => setFormData({ ...formData, timeWindowStart: e.target.value })}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '8px', display: 'block' }}>End Time</label>
                    <TextInput
                      id="timeWindowEnd"
                      placeholder="17:00"
                      value={formData.timeWindowEnd || '17:00'}
                      onChange={(e) => setFormData({ ...formData, timeWindowEnd: e.target.value })}
                    />
                  </div>
                </div>
                <label style={{ color: textPrimary, fontSize: '0.75rem', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                  Days of Week
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { day: 0, label: 'SUN' },
                    { day: 1, label: 'MON' },
                    { day: 2, label: 'TUE' },
                    { day: 3, label: 'WED' },
                    { day: 4, label: 'THU' },
                    { day: 5, label: 'FRI' },
                    { day: 6, label: 'SAT' },
                  ].map(({ day, label }) => {
                    const isSelected = (formData.timeWindowDays || []).includes(day);
                    return (
                      <Button
                        key={day}
                        kind={isSelected ? 'primary' : 'tertiary'}
                        size="sm"
                        onClick={() => {
                          const currentDays = formData.timeWindowDays || [];
                          const newDays = currentDays.includes(day)
                            ? currentDays.filter(d => d !== day)
                            : [...currentDays, day];
                          setFormData({ ...formData, timeWindowDays: newDays });
                        }}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteConfirm}
        onRequestClose={() => setDeleteConfirm(null)}
        modalHeading="Delete Rule"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleDeleteRule(deleteConfirm)}
        danger
        size="sm"
      >
        <p>Are you sure you want to delete this rule? This action cannot be undone.</p>
      </Modal>
    </div>
    </SubscriptionGate>
  );
};

export default RulesPage;
